import { Component, ViewChild, ViewChildren, QueryList, effect, input, signal, NgZone } from '@angular/core';
import { take } from 'rxjs/operators';
import { GraphsBaseComponent } from '../../graphs-base.component';
import { Utils } from '../../../core/utils';
import { getTextPositionLabel } from '../../../shared/utils/utils';
import { ChartComponent } from 'ng-apexcharts';
import {
  DataTakerStaffDataModel,
  GraphAnnotationModel,
  GraphAnnotationType,
  GraphDataModel,
  GraphGrouping,
  GraphSeriesModel,
  PatientProgramModel,
  PatientTargetModel,
  PatientTargetStatus,
  ProgramGraphType,
  TargetGraphType,
  TargetType,
} from '../../../core/api/models';
import { DatePipe } from '@angular/common';
import { GraphApiService } from '../../../core/api/services/graph-api.service';
import { AlertService } from '../../../shared/services/alert.service';
import { StaffApiService } from '../../../core/api/services/staff-api.service';
import { SupervisorApiService } from '../../../core/api/services/supervisor-api.service';
import { SelectItem } from 'primeng/api';
import { AuthService } from '../../../auth/auth.service';
import { Permissions } from '../../../core/api/models';
import { SeriesDataLabelOption, TrendLineDataLabelOption } from '../../graphs-base.component';
import { PrintService, PrintHeaderInfo } from '../../../shared/services/print.service';
@Component({
  selector: 'app-program-graph',
  templateUrl: './program-graph.component.html',
  styleUrl: './program-graph.component.css',
  standalone: false,
})
export class ProgramGraphComponent extends GraphsBaseComponent {
  @ViewChild('chart', { static: false }) chart: ChartComponent | undefined;
  @ViewChildren(ChartComponent) chartList!: QueryList<ChartComponent>;

  showAddAnnotation = signal<boolean>(false);
  annotationToEdit = signal<GraphAnnotationModel | null>(null);

  patientID = input.required<string>();
  patientIDAsNumber: number | null = null;

  startDate = signal<Date>(Utils.dateInPastBy(90));
  endDate = signal<Date>(new Date());
  forceGraphPeriod = signal<boolean>(true);

  isLoading: boolean = false;

  selectedProgram = input.required<PatientProgramModel>();

  targets: SelectItem[] = [];
  selectedTargets: PatientTargetModel[] = [];

  targetTypes: SelectItem[] = [
    {
      label: 'Anecdotal',
      value: TargetType.Anecdotal,
    },
    {
      label: 'Discrete Trial Teaching',
      value: TargetType.DiscreteTrialTeaching,
    },
    {
      label: 'Conditional Discrimination',
      value: TargetType.ConditionalDiscrimination,
    },
    {
      label: 'Duration',
      value: TargetType.Duration,
    },
    {
      label: 'Echoic',
      value: TargetType.Echoic,
    },
    {
      label: 'Fluency Rate',
      value: TargetType.FluencyRate,
    },
    {
      label: 'Frequency',
      value: TargetType.Frequency,
    },
    {
      label: 'Grouped Frequency',
      value: TargetType.GroupedFrequency,
    },
    {
      label: 'Jump To',
      value: TargetType.JumpTo,
    },
    {
      label: 'Currency',
      value: TargetType.Currency,
    },
    {
      label: 'Quantity',
      value: TargetType.Quantity,
    },
    {
      label: 'Task Analysis',
      value: TargetType.TaskAnalysis,
    },
    {
      label: 'Toileting',
      value: TargetType.Toileting,
    },
    {
      label: 'Whole Partial Interval',
      value: TargetType.WholePartialInterval,
    },
  ];
  selectedTargetType: TargetType | null = null;

  targetStatuses: SelectItem[] = [
    {
      label: 'Ready',
      value: PatientTargetStatus.Ready,
    },
    {
      label: 'Baseline',
      value: PatientTargetStatus.Baseline,
    },
    {
      label: 'Aquisition',
      value: PatientTargetStatus.Aquisition,
    },
    {
      label: 'Mastered',
      value: PatientTargetStatus.Mastered,
    },
    {
      label: 'Maintenance',
      value: PatientTargetStatus.Maintenance,
    },
    {
      label: 'Closed',
      value: PatientTargetStatus.Closed,
    },
    {
      label: 'On Hold',
      value: PatientTargetStatus.On_Hold,
    },
    {
      label: 'Fluent',
      value: PatientTargetStatus.Fluent,
    },
  ];
  selectedTargetStatus: PatientTargetStatus | null = null;

  staffMembers = signal<DataTakerStaffDataModel[]>([]);
  selectedStaffMember: DataTakerStaffDataModel | null = null;

  graphData = signal<GraphDataModel | null>(null);

  programGraphTypes: any[] = [];
  selectedProgramGraphType: ProgramGraphType | null = null;


  seriesDataLabelOption = signal<SeriesDataLabelOption>(SeriesDataLabelOption.DotsAndValues);
  trendLineDataLabelOption = signal<TrendLineDataLabelOption>(TrendLineDataLabelOption.LineOnly);
  
  seriesDataLabelOptions = [
    { label: SeriesDataLabelOption.LineOnly, value: SeriesDataLabelOption.LineOnly },
    { label: SeriesDataLabelOption.Dots, value: SeriesDataLabelOption.Dots },
    { label: SeriesDataLabelOption.DotsAndValues, value: SeriesDataLabelOption.DotsAndValues }
  ];
  
  trendLineDataLabelOptions = [
    { label: TrendLineDataLabelOption.LineOnly, value: TrendLineDataLabelOption.LineOnly },
    { label: TrendLineDataLabelOption.Dots, value: TrendLineDataLabelOption.Dots },
    { label: TrendLineDataLabelOption.DotsAndValues, value: TrendLineDataLabelOption.DotsAndValues }
  ];
  displayLegend = signal<boolean>(true);
  displayAnnotations = signal<boolean>(true);
  displayAutomaticAnnotations = signal<boolean>(true);
  displayUserAnnotations = signal<boolean>(true);

  displayTrendLine = signal<boolean>(false);

  annotations = signal<GraphAnnotationModel[]>([]);

  private dateChangeTimer: any;

  isPrintingPdf = false;
  isPrintingPng = false;
  private readonly groupedExportPreparationDelayMs = 7000;
  private readonly syncedExportPreparationDelayMs = 2000;

  constructor(
    private graphApiService: GraphApiService,
    private supervisorApiService: SupervisorApiService,
    private staffApiService: StaffApiService,
    private datePipe: DatePipe,
    private alertService: AlertService,
    private authService: AuthService,
    private printService: PrintService,
    private ngZone: NgZone
  ) {
    super();
    this.loadDefaultGraphOptions();
    this.isGrouped.set(false);

    // Set default date option to 3 months (index 6)
    this.dateOption = this.dateOptions[6].value;

    effect(() => {
      console.log(
        'programId changed to:',
        this.selectedProgram()?.ID ?? 'All Programs'
      );
      if (this.selectedProgramGraphType == null) {
        this.selectedProgramGraphType = ProgramGraphType.AllTargets;
      }
      this.loadData();
    });
  }

  ngOnInit(): void {

    this.groupingOptions = this.groupingOptionsIncludingStaffOption;
    this.groupingOption = this.groupingOptions[2].value; // Daily

    this.patientIDAsNumber = Number(this.patientID());
    this.isLoading = true;
    if (this.selectedProgram()) {
      this.targets =
        this.selectedProgram()!.Targets?.map((target) => ({
          text:
            target.TargetName +
            ' (' +
            Utils.patientTargetTypeLabel(target?.TargetType!) +
            ')',
          value: target,
        })) ?? [];
    }
  }

  loadData() {
    this.loadDataForGraph();
  }

  onTargetTypeChange(event: any) {
    this.selectedTargetType = event.value;
    this.loadDataForGraph();
  }

  onTargetStatusChange(event: any) {
    this.selectedTargetStatus = event.value;
    this.loadDataForGraph();
  }

  onProgramGraphTypeChange(event: any) {
    this.selectedProgramGraphType = event.value;
    this.loadDataForGraph();
  }

  onSelectedTargetsChange(event: any) {
    this.selectedTargets = event.value;
    this.loadDataForGraph();
  }

  hasSupervisorPermission(): boolean {
    const userInfo = this.authService.userInfo;
    if (!userInfo || !userInfo.Permissions) {
      return false;
    }

    return userInfo.Permissions.includes(Permissions.SparkApp_Supervisor);
  }

  shouldDisplayAddAnnotationButton(): boolean {
    return this.hasSupervisorPermission();
  }

  updateAvailableProgramGraphTypes() {
    this.programGraphTypes = [
      {
        text: 'All Targets',
        value: ProgramGraphType.AllTargets,
      },
      {
        text: 'Filter By Target Type',
        value: ProgramGraphType.FilteredByTargetType,
      },
      {
        text: 'Filter By Target Status',
        value: ProgramGraphType.FilteredByTargetStatus,
      },
      {
        text: 'Selected Targets',
        value: ProgramGraphType.SelectedTargets,
      },
      {
        text: 'Targets Mastered Cumulative',
        value: ProgramGraphType.TargetsMasteredCumulative,
      },
    ];
  }

  loadDataForGraph() {
    this.updateAvailableProgramGraphTypes();

    var startTime = this.startDate();
    var endTime = this.endDate();

    this.isLoading = true;

    this.graphApiService
      .GetProgramGraph({
        patientID: Number(this.patientID()),
        programID: this.selectedProgram()?.ID ?? 0,
        targetIDs: this.selectedTargets?.map((target) => target.ID!),
        startTime: startTime.toISOString(),
        endTime: endTime.toISOString(),
        programGraphType: this.selectedProgramGraphType!,
        targetTypeToFilter: this.selectedTargetType ?? undefined,
        targetStatusToFilter: this.selectedTargetStatus ?? undefined,
        graphGrouping: this.groupingOption,
        staffID: this.selectedStaffMember?.StaffID,
        includeTrendLine: this.displayTrendLine(),
      })
      .subscribe((data) => {
        console.log(data);

        this.staffMembers.set(data.DataTakers ?? []);

        // Add placeholder data points for the start and end of the selected period
        if (this.forceGraphPeriod()) {
          const startDate = this.startDate().toISOString();
          const endDate = this.endDate().toISOString();

          if (data.Series && data.Series.length > 0) {
            for (const series of data.Series) {
              if (series.DataPoints && series.DataPoints.length > 0) {
                const firstDataPoint = series.DataPoints[0];
                const lastDataPoint =
                  series.DataPoints[series.DataPoints.length - 1];

                if (firstDataPoint.Date !== startDate) {
                  series.DataPoints.unshift({
                    Date: startDate,
                    Value: null,
                  });
                }

                if (lastDataPoint.Date !== endDate) {
                  series.DataPoints.push({ Date: endDate, Value: null });
                }
              } else {
                // If no points in series, add placeholders for the entire period
                series.DataPoints = [
                  { Date: startDate, Value: null },
                  { Date: endDate, Value: null },
                ];
              }
            }
          }
        }

        this.graphData.set(data);
        this.annotations.set(data.Annotations ?? []);
        this.updateGraph();
        this.isLoading = false;
      });
  }

  shouldDisplaySelectedTargetsOptions(): boolean {
    return this.selectedProgramGraphType == ProgramGraphType.SelectedTargets;
  }

  shouldDisplayTargetTypeFilterOptions(): boolean {
    return (
      this.selectedProgramGraphType == ProgramGraphType.FilteredByTargetType
    );
  }

  shouldDisplayTargetStatusFilterOptions(): boolean {
    return (
      this.selectedProgramGraphType == ProgramGraphType.FilteredByTargetStatus
    );
  }

  shouldDisplayTrendLineOption(): boolean {
    return (
      this.selectedProgramGraphType !=
      ProgramGraphType.TargetsMasteredCumulative
    );
  }

  shouldDisplayGroupingOptions(): boolean {
    return (
      this.selectedProgramGraphType !=
      ProgramGraphType.TargetsMasteredCumulative
    );
  }

  shouldDisplayStaffMemberOptions(): boolean {
    return (
      this.staffMembers.length > 1 &&
      this.groupingOption != GraphGrouping.ByStaffMember
    );
  }

  shouldDisplayDisplayOptions(): boolean {
    return true;
  }

  shouldDisplayGraph(): boolean {
    return (
      this.isLoading === false &&
      this.selectedProgram != null &&
      !this.isGraphEmpty()
    );
  }

  shouldDisplayToggleGrouped(): boolean {
    return this.selectedProgramGraphType != ProgramGraphType.TargetsMasteredCumulative && this.groupingOption != GraphGrouping.ByStaffMember;
  }

  isGraphEmpty(): boolean {
    // check if the graph data is empty
    if (this.graphData() == null) {
      return true;
    }

    // check if series is empty
    if (this.graphData()?.Series?.length == 0) {
      return true;
    }

    return false;
  }

  formatDateForInput(date: Date): string {
    return Utils.formatDateForInput(date);
  }

  onStartDateChange(event: any) {
    if (!event) return;

    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }

    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      this.startDate.set(date);
      this.dateOption = this.dateOptions[0].value; // Custom option value
    }, 1500); // Wait 1.5 seconds after last change
  }

  onEndDateChange(event: any) {
    if (!event) return;

    // Clear any existing timer
    if (this.dateChangeTimer) {
      clearTimeout(this.dateChangeTimer);
    }

    // Set new timer
    this.dateChangeTimer = setTimeout(() => {
      const date = Utils.parseDateFromInput(event);
      const endOfDay = new Date(date);
      endOfDay.setHours(23, 59, 59, 999);
      this.endDate.set(endOfDay);
      this.dateOption = this.dateOptions[0].value; // Custom option value
    }, 1500); // Wait 1.5 seconds after last change
  }

  onDateChange(event: any) {
    const selectedDate = event.target.valueAsDate;
    // date is coming in UTC, we need to convert it to local time
    const localDate = new Date(
      selectedDate.getTime() + selectedDate.getTimezoneOffset() * 60000
    );
    const localDateEndOfDay = new Date(
      localDate.getTime() +
        23 * 60 * 60 * 1000 +
        59 * 60 * 1000 +
        59 * 1000 +
        999
    );

    this.startDate.set(localDate);
    this.endDate.set(localDateEndOfDay);
    //this.loadDataForGraph();
  }

  onDateOptionChange(event: any) {
    // Don't update if custom is selected
    if (event.value === this.dateOptions[0].value) {
      return;
    }

    // Update start date based on the selected option
    this.startDate.set(event.value);

    // Set end date to end of day today
    const endOfDayToday = new Date(
      new Date().getTime() +
        23 * 60 * 60 * 1000 +
        59 * 60 * 1000 +
        59 * 1000 +
        999
    );
    this.endDate.set(endOfDayToday);

    // Update the graph
    //this.loadDataForGraph();
  }

  onGroupingOptionChange(event: any) {
    this.groupingOption = event.value;

    if (this.groupingOption == GraphGrouping.ByStaffMember) {
      this.selectedStaffMember = null;
    }

    this.loadDataForGraph();
  }

  onForceGraphPeriodChange(checked: boolean) {
    this.forceGraphPeriod.set(checked);
    this.loadDataForGraph();
  }

  onStaffMemberChange(event: any) {
    this.selectedStaffMember = event.value;
    this.loadDataForGraph();
  }

  override getDataLabelsConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    
    // Check if this is a multi-series graph (more than 2 series or no trendline)
    const isMultiSeries = !this.displayTrendLine() || (this.graphData()?.Series?.length ?? 0) > 2;
    
    if (isMultiSeries) {
      // For multi-series graphs, apply configuration to all series
      return {
        enabled: true,
        enabledOnSeries: seriesOption !== SeriesDataLabelOption.LineOnly ? undefined : [],
        formatter: (value: any, { seriesIndex, dataPointIndex, w }: any) => {
          const shouldShowValues = seriesOption === SeriesDataLabelOption.DotsAndValues;
          return shouldShowValues ? (value ? value : '') : '';
        }
      };
    } else {
      // For single-series graphs with trendline, use array-based configuration
      return {
        enabled: true,
        enabledOnSeries: this.getEnabledOnSeries(seriesOption, trendLineOption),
        formatter: (value: any, { seriesIndex, dataPointIndex, w }: any) => {
          // Check if this series should show values
          const isSeries = seriesIndex === 0;
          const shouldShowValues = isSeries ? 
            seriesOption === SeriesDataLabelOption.DotsAndValues :
            trendLineOption === TrendLineDataLabelOption.DotsAndValues;

          return shouldShowValues ? (value ? value : '') : '';
        }
      };
    }
  }

  getEnabledOnSeries(seriesOption: SeriesDataLabelOption, trendLineOption: TrendLineDataLabelOption): number[] {
    const enabled: number[] = [];
    
    // Series (index 0) - enabled if not LineOnly
    if (seriesOption !== SeriesDataLabelOption.LineOnly) {
      enabled.push(0);
    }
    
    // Trend line (index 1) - enabled if not LineOnly and trend line is displayed
    if (this.displayTrendLine() && trendLineOption !== TrendLineDataLabelOption.LineOnly) {
      enabled.push(1);
    }
    
    return enabled;
  }

  override getMarkersConfig(): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();

    // Check if this is a multi-series graph (more than 2 series or no trendline)
    const isMultiSeries = !this.displayTrendLine() || (this.graphData()?.Series?.length ?? 0) > 2;
    
    if (isMultiSeries) {
      // For multi-series graphs, apply configuration to all series
      const seriesSize = seriesOption === SeriesDataLabelOption.LineOnly ? 0 : 3;
      return {
        size: seriesSize,
        strokeColors: '#fff',
        strokeWidth: seriesSize > 0 ? 2 : 0,
        strokeOpacity: seriesSize > 0 ? 0.9 : 0,
        fillOpacity: seriesSize > 0 ? 1 : 0,
        shape: 'square',
        showNullDataPoints: false,
      };
    } else {
      // For single-series graphs with trendline, use array-based configuration
      const seriesSize = seriesOption === SeriesDataLabelOption.LineOnly ? 0 : 3;
      const trendLineSize = trendLineOption === TrendLineDataLabelOption.LineOnly ? 0 : 3;

      return {
        size: [seriesSize, trendLineSize],
        strokeColors: '#fff',
        strokeWidth: [seriesSize > 0 ? 2 : 0, trendLineSize > 0 ? 2 : 0],
        strokeOpacity: [seriesSize > 0 ? 0.9 : 0, trendLineSize > 0 ? 0.9 : 0],
        fillOpacity: [seriesSize > 0 ? 1 : 0, trendLineSize > 0 ? 1 : 0],
        shape: 'square',
        showNullDataPoints: false,
      };
    }
  }

  // For multi-series charts where we know the exact number of series (e.g., grouped by staff per target),
  // adapt markers to apply selection consistently across all series in that chart
  private getMarkersConfigForSeriesCount(seriesCount: number): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();

    const hasTrendLine = this.displayTrendLine();

    if (hasTrendLine) {
      // With trendline, series alternate: series1, trendline1, series2, trendline2, etc.
      const seriesSize = seriesOption === SeriesDataLabelOption.LineOnly ? 0 : 3;
      const trendSize = trendLineOption === TrendLineDataLabelOption.LineOnly ? 0 : 3;
      
      // Create arrays for all series, alternating between series and trendline sizes
      const sizes: number[] = [];
      const strokeWidths: number[] = [];
      const strokeOpacities: number[] = [];
      const fillOpacities: number[] = [];
      
      for (let i = 0; i < seriesCount; i++) {
        if (i % 2 === 0) {
          // Even indices are actual series
          sizes.push(seriesSize);
          strokeWidths.push(seriesSize > 0 ? 2 : 0);
          strokeOpacities.push(seriesSize > 0 ? 0.9 : 0);
          fillOpacities.push(seriesSize > 0 ? 1 : 0);
        } else {
          // Odd indices are trendlines
          sizes.push(trendSize);
          strokeWidths.push(trendSize > 0 ? 2 : 0);
          strokeOpacities.push(trendSize > 0 ? 0.9 : 0);
          fillOpacities.push(trendSize > 0 ? 1 : 0);
        }
      }
      
      return {
        size: sizes,
        strokeColors: '#fff',
        strokeWidth: strokeWidths,
        strokeOpacity: strokeOpacities,
        fillOpacity: fillOpacities,
        shape: 'square',
        showNullDataPoints: false,
      };
    }

    // No trendline: apply the same size to all series in the chart
    const s = seriesOption === SeriesDataLabelOption.LineOnly ? 0 : 3;
    const strokeWidth = s > 0 ? 2 : 0;
    const strokeOpacity = s > 0 ? 0.9 : 0;
    const fillOpacity = s > 0 ? 1 : 0;
    
    return {
      size: Array(seriesCount).fill(s),
      strokeColors: '#fff',
      strokeWidth: Array(seriesCount).fill(strokeWidth),
      strokeOpacity: Array(seriesCount).fill(strokeOpacity),
      fillOpacity: Array(seriesCount).fill(fillOpacity),
      shape: 'square',
      showNullDataPoints: false,
    };
  }

  // For multi-series charts where we know the exact number of series, adapt data labels configuration
  private getDataLabelsConfigForSeriesCount(seriesCount: number): any {
    const seriesOption = this.seriesDataLabelOption();
    const trendLineOption = this.trendLineDataLabelOption();
    const hasTrendLine = this.displayTrendLine();

    const showsValues = (opt: SeriesDataLabelOption | TrendLineDataLabelOption) => opt === SeriesDataLabelOption.DotsAndValues || opt === TrendLineDataLabelOption.DotsAndValues;

    if (hasTrendLine) {
      // With trendline, series alternate: series1, trendline1, series2, trendline2, etc.
      const enabledIndices: number[] = [];
      
      for (let i = 0; i < seriesCount; i++) {
        if (i % 2 === 0) {
          // Even indices are actual series
          if (seriesOption !== SeriesDataLabelOption.LineOnly) {
            enabledIndices.push(i);
          }
        } else {
          // Odd indices are trendlines
          if (trendLineOption !== TrendLineDataLabelOption.LineOnly) {
            enabledIndices.push(i);
          }
        }
      }
      
      return {
        enabled: enabledIndices.length > 0,
        enabledOnSeries: enabledIndices,
        formatter: (value: any, ctx: any) => {
          const idx = ctx?.seriesIndex ?? 0;
          const isSeries = idx % 2 === 0; // Even indices are series, odd are trendlines
          const shouldShowValues = isSeries ? showsValues(seriesOption) : showsValues(trendLineOption);
          return shouldShowValues ? (value ? value : '') : '';
        }
      };
    }

    // No trendline: apply to all series equally
    if (seriesOption === SeriesDataLabelOption.LineOnly) {
      return { enabled: false };
    }
    
    // Enable data labels for all series in the chart
    const enabledIndices = Array.from({ length: seriesCount }, (_, i) => i);
    return {
      enabled: true,
      enabledOnSeries: enabledIndices,
      formatter: (value: any) => (seriesOption === SeriesDataLabelOption.DotsAndValues ? (value ? value : '') : '')
    };
  }

  onDisplayLegendChange() {
    this.updateGraph();
  }

  onToggleGrouped() {
    this.updateGraph();
  }

  onToggleTrendLine() {
    this.loadDataForGraph();
  }

  updateGraph() {
    console.log('updateGraph start - ' + new Date().toISOString());
    if (!this.graphData()) {
      // hide graph
      this.chartOptions.series = [];
      return;
    }
    switch (this.selectedProgramGraphType) {
      case ProgramGraphType.TargetsMasteredCumulative:
        this.loadDefaultLineGraphOptionsForPrograms(
          this.graphData()!, 
          this.seriesDataLabelOption(), 
          this.displayLegend(), 
          this.displayAnnotations(), 
          this.displayTrendLine(),
          false,
          false,
          false,
          true
        );
        break;
      case ProgramGraphType.AllTargets:
      case ProgramGraphType.FilteredByTargetType:
      case ProgramGraphType.FilteredByTargetStatus:
      case ProgramGraphType.SelectedTargets:
        // check if the selected targets are all prompt based to set the y axis min and max to 0 and 100
        const allPromptBased = this.selectedTargets.every(target => target.TargetType == TargetType.Echoic || target.TargetType == TargetType.DiscreteTrialTeaching);
        this.loadDefaultLineGraphOptionsForPrograms(this.graphData()!, this.seriesDataLabelOption(), this.displayLegend(), this.displayAnnotations(), this.displayTrendLine(), allPromptBased, false, true, false);
        break;
      default:
        break;
    }

    // Set a default filename immediately to prevent random filename generation
    this.setDefaultExportFilename();

    this.chart?.updateOptions(this.chartOptions);

    console.log('updateGraph end - ' + new Date().toISOString());
  }

  private setDefaultExportFilename(): void {
    // Set a basic filename immediately to prevent ApexCharts from generating a random one
    const graphType = this.getProgramGraphTypeDisplayName() || 'Graph';
    const programName = this.selectedProgram()?.ProgramName || 'Program';
    const defaultFilename = `${graphType}-${programName}`.replace(/[^a-zA-Z0-9\s-]/g, '').replace(/\s+/g, '_').substring(0, 100);
    
    if (this.chartOptions.chart?.toolbar?.export) {
      if (!this.chartOptions.chart.toolbar.export.csv) {
        this.chartOptions.chart.toolbar.export.csv = {};
      }
      if (!this.chartOptions.chart.toolbar.export.svg) {
        this.chartOptions.chart.toolbar.export.svg = {};
      }
      if (!this.chartOptions.chart.toolbar.export.png) {
        this.chartOptions.chart.toolbar.export.png = {};
      }
      this.chartOptions.chart.toolbar.export.csv.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.svg.filename = defaultFilename;
      this.chartOptions.chart.toolbar.export.png.filename = defaultFilename;
    }
  }

  onSeriesDataLabelOptionChange() {
    this.updateGraph();
  }

  onTrendLineDataLabelOptionChange() {
    this.updateGraph();
  }

  // Annotation functions

  addAnnotationButtonClicked() {
    console.log('addAnnotationButtonClicked');
    const emptyAnnotation: GraphAnnotationModel = {
      PatientProgramIDs: [this.selectedProgram()!.ID!],
    };
    this.annotationToEdit.set(emptyAnnotation);
    this.showAddAnnotation.set(true);
  }

  onAnnotationVisibilityChange(annotation: GraphAnnotationModel) {
    console.log('onAnnotationVisibilityChange', annotation);
    // update the graph data with the new value for the changed annotation
    if (annotation.IsAutomatic) {
      this.graphData()!.Annotations!.find(
        (a) => a.Label === annotation.Label
      )!.VisibleForUser = annotation.VisibleForUser;
    } else {
      this.graphData()!.Annotations!.find(
        (a) => a.ID === annotation.ID
      )!.VisibleForUser = annotation.VisibleForUser;
      // update the visibility preference for this user on the backend
      this.graphApiService
        .SaveGraphAnnotation({
          body: annotation,
        })
        .subscribe((data) => {
          console.log('SaveGraphAnnotation visibility preference', data);
        });
    }
    this.updateGraph();
  }

  editAnnotation(annotation: GraphAnnotationModel) {
    console.log('editAnnotation', annotation);
    this.annotationToEdit.set(annotation);
    this.showAddAnnotation.set(true);
  }

  annotationLabel(annotation: GraphAnnotationModel) {
    switch (annotation.GraphAnnotationType) {
      case GraphAnnotationType.Point:
        // For Point type: Label - Date - Y Value - Type
        return (
          annotation.Label +
          ' - ' +
          new Date(annotation.StartDate!).toLocaleDateString() +
          ' - ' +
          annotation.YValueStart +
          ' - ' +
          Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!)
        );
      case GraphAnnotationType.Text:
        // For Text type: Label - Position (or coordinates) - Type
        const textPosition = this.getTextPositionLabel(annotation.YValueStart, annotation.YValueEnd);
        return (
          annotation.Label +
          ' - ' +
          textPosition +
          ' - ' +
          Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!)
        );
      case GraphAnnotationType.XAxis:
        return (
          annotation.Label +
          ' - ' +
          Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!) +
          ' - ' +
          new Date(annotation.StartDate!).toLocaleDateString()
        );
      case GraphAnnotationType.XAxisRange:
        return (
          annotation.Label +
          ' - ' +
          Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!) +
          ' - ' +
          new Date(annotation.StartDate!).toLocaleDateString() +
          ' - ' +
          new Date(annotation.EndDate!).toLocaleDateString()
        );
      case GraphAnnotationType.YAxis:
        return (
          annotation.Label +
          ' - ' +
          Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!) +
          ' - ' +
          annotation.YValueStart
        );
      case GraphAnnotationType.YAxisRange:
        return (
          annotation.Label +
          ' - ' +
          Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!) +
          ' - ' +
          annotation.YValueStart +
          ' - ' +
          annotation.YValueEnd
        );
      default:
        return (
          annotation.Label +
          ' - ' +
          Utils.graphAnnotationTypeLabel(annotation.GraphAnnotationType!)
        );
    }
  }

  private getTextPositionLabel(x: number | null | undefined, y: number | null | undefined): string {
    return getTextPositionLabel(x, y);
  }

  onCloseAddAnnotation(withChanges: boolean = false) {
    console.log('onCloseAddAnnotation', withChanges);
    if (withChanges) {
      this.loadData();
    }
    this.showAddAnnotation.set(false);
  }

  automaticAnnotations() {
    if (this.graphData() == null || this.graphData()!.Annotations == null) {
      return [];
    }
    return this.graphData()!.Annotations!.filter((a) => a.IsAutomatic);
  }

  userAnnotations() {
    if (this.graphData() == null || this.graphData()!.Annotations == null) {
      return [];
    }
    return this.graphData()!.Annotations!.filter((a) => !a.IsAutomatic);
  }

  onToggleAutomaticAnnotations() {
    // go through all automatic annotations and toggle their visibility according to the new value of displayAutomaticAnnotations
    this.automaticAnnotations().forEach(
      (a) => (a.VisibleForUser = this.displayAutomaticAnnotations())
    );
    this.updateGraph();
  }

  onToggleUserAnnotations() {
    this.userAnnotations().forEach(
      (a) => (a.VisibleForUser = this.displayUserAnnotations())
    );
    this.updateGraph();
  }

  onDisplayAnnotationsChange() {
    this.annotations().forEach(
      (a) => (a.VisibleForUser = this.displayAnnotations())
    );
    this.displayUserAnnotations.set(this.displayAnnotations());
    this.displayAutomaticAnnotations.set(this.displayAnnotations());
    this.updateGraph();
  }

  // graph functions
  loadDefaultLineGraphOptionsForPrograms(
    graphData: GraphDataModel, 
    displayDataPointOption: SeriesDataLabelOption = SeriesDataLabelOption.DotsAndValues, 
    displayLegend: boolean = true, 
    displayAnnotations: boolean = true, 
    displayTrendLineForProgramGraph: boolean = false,
    limitYAxisValues: boolean = false,
    displayTotalSessionTimeAndTargetDataTimeInTooltip: boolean = false,
    displayPromptDataInTooltip: boolean = false,
    displayMasteredTargetsTooltip: boolean = false
  ) {
    if (this.isGrouped()) {
      // Grouped Graph or Normal Graphs
      console.log('Program Graph - Grouped Graph');
      this.isGrouped.set(true);
      this.chartOptions = {
        series: graphData.Series!.map((series) => ({
          name: series.Name ?? 'No Name',
          data: this.processSeriesDataWithBreaks(series, graphData).data,
        })),
        legend: displayLegend
          ? {
              position: 'top',
              onItemClick: {
                toggleDataSeries: true,
              },
              onItemHover: {
                highlightDataSeries: true,
              },
            }
          : {
              show: false,
            },
        chart: {
          height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
          width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
          type: 'line',
          ...this.sharedChartConfig,
        },
        dataLabels: this.getDataLabelsConfig(),
        stroke: {
          curve: 'straight',
        },
        grid: {
          padding: {
            right: 30,
            left: 20,
          },
        },
        markers: this.getMarkersConfig(),
        tooltip: displayMasteredTargetsTooltip ? { custom: this.createCustomTooltipMasteredTargets(graphData) } : displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
          custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, this.staffMembers()) : this.createCustomTooltipTime(graphData)
        } : {
          y: {
            formatter: (value: any) => {
              if (value === null) {
                return '';
              }
              return value;
            },
          },
        },
        title: {
          text: graphData.Title ?? 'Chart',
        },
        xaxis: {
          type: 'datetime',
          title: {
            text: graphData.XAxisTitle ?? 'X Axis',
          },
          labels: {
            datetimeUTC: false,
          },
        },
        yaxis: {
          min: limitYAxisValues ? 0 : undefined,
          max: limitYAxisValues ? 100 : undefined,
          title: {
            text: graphData.YAxisTitle ?? 'Y Axis',
          },
          labels: {
            formatter: (value: number) => {
              // If it's a whole number, don't show decimal places
              if (Number.isInteger(value)) {
                return value.toString();
              }
              // If it's a decimal, round to 2 decimal places
              return value.toFixed(2);
            }
          }
        },
        annotations: displayAnnotations
          ? {
              xaxis: this.xaxisAnnotationsFromGraphData(graphData),
              yaxis: this.yaxisAnnotationsFromGraphData(graphData),
              points: this.pointsAnnotationsFromGraphData(graphData),
              texts: this.textsAnnotationsFromGraphData(graphData),
            }
          : undefined,
      };
    } else {
      // Synced Graphs
      this.isGrouped.set(false);

      // if grouping option is by staff member, we need to group the graphs by staff member
      if (this.groupingOption === GraphGrouping.ByStaffMember) {
        console.log('Program Graph - Synced Graphs - By Staff Member (group by PatientTargetID)');
        // Group series by PatientTargetID so each chart shows all staff series for that target
        const seriesByTargetId = new Map<number, GraphSeriesModel[]>();
        (graphData.Series || []).forEach((s) => {
          const targetId = s.PatientTargetID as number | undefined;
          if (targetId == null) return;
          const arr = seriesByTargetId.get(targetId) || [];
          arr.push(s);
          seriesByTargetId.set(targetId, arr);
        });
        this.chartsOptions = Array.from(seriesByTargetId.entries()).map(([targetId, seriesList]) => {
          const targetName = seriesList[0].PatientTargetName ?? 'No Name';
          return {
            series: seriesList.map((s) => ({
              name: s.Name ?? 'No Name',
              data: this.processSeriesDataWithBreaks(s, graphData).data,
            })),
            legend: displayLegend ? {
              position: 'top',
              onItemClick: { toggleDataSeries: true },
              onItemHover: { highlightDataSeries: true },
            } : { show: false },
            chart: {
              id: `target-${targetId}`,
              group: 'group1',
              height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
              width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
              type: 'line',
              ...this.sharedChartConfig,
            },
            dataLabels: this.getDataLabelsConfigForSeriesCount(seriesList.length),
            stroke: { curve: 'straight' },
            grid: { padding: { right: 30, left: 20 } },
            markers: this.getMarkersConfigForSeriesCount(seriesList.length),
            tooltip: displayMasteredTargetsTooltip ? { custom: this.createCustomTooltipMasteredTargets(graphData) } : (displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip) ? {
              custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, this.staffMembers()) : this.createCustomTooltipTime(graphData)
            } : {
              x: { format: 'MMM dd yyyy' },
              y: {
                formatter: (value: any) => (value === null ? '' : value),
              },
            },
            title: {
              text: `Target ${targetName}`,
            },
            xaxis: {
              type: 'datetime',
              title: { text: graphData.XAxisTitle ?? 'X Axis' },
              labels: { datetimeUTC: false },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: { text: graphData.YAxisTitle ?? 'Y Axis' },
            },
            annotations: displayAnnotations && seriesList.length > 0 ? {
              xaxis: this.xaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, seriesList[0]),
              yaxis: this.yaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, seriesList[0]),
              points: this.pointsAnnotationsFromGraphDataForSyncedPrograms(graphData, seriesList[0]),
              texts: this.textsAnnotationsFromGraphDataForSyncedPrograms(graphData, seriesList[0]),
            } : undefined,
          };
        });
        console.log('Program Graph - Synced Graphs - Grouped by TargetID - Charts Options', this.chartsOptions);
      }
      else {
        // if it is a program graph and trend line is selected, we need to graph types two by two;
        // every other graph in the series will be the trend line in this case;
        if (displayTrendLineForProgramGraph) { // Program Graph with Trend Line
          console.log('Program Graph with Trend Line');
          var allSeries = graphData.Series!;
          var notTrendLineSeries = allSeries.filter((series, index) => index % 2 === 0);
          var trendLineSeries = allSeries.filter((series, index) => index % 2 === 1);

        this.chartsOptions = notTrendLineSeries.map((series) => {
          var trendLineSeriesForGraph = trendLineSeries.shift();
          return {
            series: [
              {
                name: series.Name ?? 'No Name',
                data: this.processSeriesDataWithBreaks(series, graphData).data,
              },
              // pop the first series from the trendLineSeries array and add it to the series array
              {
                name: trendLineSeriesForGraph!.Name ?? 'No Name',
                data: trendLineSeriesForGraph!.DataPoints!.map(
                  (data) =>
                    [
                      data.Date
                        ? new Date(data.Date).getTime()
                        : new Date().getTime(),
                      data.Value,
                    ] as [number, number | null]
                ),
              },
            ],
            legend: displayLegend
              ? {
                  position: 'top',
                  onItemClick: {
                    toggleDataSeries: true,
                  },
                  onItemHover: {
                    highlightDataSeries: true,
                  },
                }
              : {
                  show: false,
                },
            chart: {
              id: series.Name ?? 'No Name',
              group: 'group1',
              height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
              width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
              type: 'line',
              ...this.sharedChartConfig,
            },
            dataLabels: this.getDataLabelsConfigForSeriesCount(2),
            stroke: {
              curve: 'straight',
            },
            grid: {
              padding: {
                right: 30,
                left: 20,
              },
            },
            markers: this.getMarkersConfigForSeriesCount(2),
            tooltip: displayMasteredTargetsTooltip ? { custom: this.createCustomTooltipMasteredTargets(graphData) } : displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
              custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, this.staffMembers()) : this.createCustomTooltipTime(graphData)
            } : {
              x: {
                format: 'MMM dd yyyy',
              },
              y: {
                formatter: (value: any) => {
                  if (value === null) {
                    return '';
                  }
                  return value;
                },
              },
            },
            title: {
              text: series.Name ?? 'Chart',
            },
            xaxis: {
              type: 'datetime',
              title: {
                text: graphData.XAxisTitle ?? 'X Axis',
              },
              labels: {
                datetimeUTC: false,
              },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: {
                text: graphData.YAxisTitle ?? 'Y Axis',
                },
              labels: {
                formatter: (value: number) => {
                  // If it's a whole number, don't show decimal places
                  if (Number.isInteger(value)) {
                    return value.toString();
                  }
                  // If it's a decimal, round to 2 decimal places
                  return value.toFixed(2);
                }
              }
              },
              annotations: displayAnnotations ? {
                xaxis: this.xaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
                yaxis: this.yaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
                points: this.pointsAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
                texts: this.textsAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
              } : undefined,
          }
        });
      } else {
        // Synced Graphs
        console.log('Program Graph - Synced Graphs');
        this.chartsOptions = graphData.Series!.map((series) => {
          return {
            series: [
              {
                name: series.Name ?? 'No Name',
                data: this.processSeriesDataWithBreaks(series, graphData).data,
              },
            ],
            legend: displayLegend
              ? {
                  position: 'top',
                  onItemClick: {
                    toggleDataSeries: true,
                  },
                  onItemHover: {
                    highlightDataSeries: true,
                  },
                }
              : {
                  show: false,
                },
            chart: {
              id: series.Name ?? 'No Name',
              group: 'group1',
              height: this.heightForChart(this.isPrintingPdf, this.isPrintingPng),
              width: this.widthForChart(this.isPrintingPdf, this.isPrintingPng),
              type: 'line',
              ...this.sharedChartConfig,
            },
            dataLabels: this.getDataLabelsConfig(),
            stroke: {
              curve: 'straight',
            },
            grid: {
              padding: {
                right: 30,
                left: 20,
              },
            },
            markers: this.getMarkersConfig(),
            tooltip: displayMasteredTargetsTooltip ? { custom: this.createCustomTooltipMasteredTargets(graphData) } : displayTotalSessionTimeAndTargetDataTimeInTooltip || displayPromptDataInTooltip ? {
              custom: displayPromptDataInTooltip ? this.createCustomTooltipPrompt(graphData, this.staffMembers()) : this.createCustomTooltipTime(graphData)
            } : {
              x: {
                format: 'MMM dd yyyy',
              },
              y: {
                formatter: (value: any) => {
                  if (value === null) {
                    return '';
                  }
                  return value;
                },
              },
            },
            title: {
              text: series.Name ?? 'Chart',
            },
            xaxis: {
              type: 'datetime',
              title: {
                text: graphData.XAxisTitle ?? 'X Axis',
              },
              labels: {
                datetimeUTC: false,
              },
            },
            yaxis: {
              min: limitYAxisValues ? 0 : undefined,
              max: limitYAxisValues ? 100 : undefined,
              title: {
                text: graphData.YAxisTitle ?? 'Y Axis',
                },
              labels: {
                formatter: (value: number) => {
                  // If it's a whole number, don't show decimal places
                  if (Number.isInteger(value)) {
                    return value.toString();
                  }
                  // If it's a decimal, round to 2 decimal places
                  return value.toFixed(2);
                }
              }
              },
            annotations: displayAnnotations ? {
              xaxis: this.xaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
              yaxis: this.yaxisAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
              points: this.pointsAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
              texts: this.textsAnnotationsFromGraphDataForSyncedPrograms(graphData, series),
            } : undefined,
          }
        });
      }
      }
    }
  }

  // annotation graph functions
  // Annotations
  xaxisAnnotationsFromGraphDataForSyncedPrograms(
    graphData: GraphDataModel,
    series: GraphSeriesModel
  ) {
    var annotationsForXAxis = graphData?.Annotations?.filter(
      (annotation) =>
        annotation.GraphAnnotationType === GraphAnnotationType.XAxis ||
        annotation.GraphAnnotationType === GraphAnnotationType.XAxisRange
    );

    // we need to filter the annotations to only include the ones for the target being graphed
    if (series.PatientTargetID) {
      annotationsForXAxis = annotationsForXAxis?.filter(
        (annotation) =>
          annotation.PatientTargetIDs?.includes(series.PatientTargetID!) ||
          (annotation.PatientProgramIDs?.length ?? 0) > 0
      );
    }

    return annotationsForXAxis?.map((annotation) => {
      if (annotation.VisibleForUser) {
        if (annotation.GraphAnnotationType === GraphAnnotationType.XAxis) {
          return {
            x: new Date(annotation.StartDate!).getTime(),
            strokeDashArray: 5,
            borderColor: annotation.Color ?? '#008FFB',
            strokeOpacity: 0.3,
            borderWidth: 1,
            label: {
              borderColor: annotation.Color ?? '#008FFB',
              style: {
                color: annotation.Color ? `${annotation.Color}80` : '#008FFB80',
                fontSize: '14px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                opacity: 0.2,
                background: 'rgba(255, 255, 255, 0.05)',
                textAlign: 'left',
              },
              text: this.formatAnnotationLabelText(
                annotation.Label ?? ''
              ) as any,
            },
          };
        } else {
          return {
            x: new Date(annotation.StartDate!).getTime(),
            x2: new Date(annotation.EndDate!).getTime(),
            strokeDashArray: 5,
            borderColor: annotation.Color ?? '#008FFB',
            strokeOpacity: 0.3,
            borderWidth: 1,
            fillOpacity: 0.1,
            fillColor: annotation.Color ?? '#008FFB',
            label: {
              borderColor: annotation.Color ?? '#008FFB',
              style: {
                color: annotation.Color ? `${annotation.Color}80` : '#008FFB80',
                fontSize: '14px',
                fontWeight: 'bold',
                fontFamily: 'monospace',
                opacity: 0.2,
                background: 'rgba(255, 255, 255, 0.05)',
                textAlign: 'left',
              },
              text: this.formatAnnotationLabelText(
                annotation.Label ?? ''
              ) as any,
            },
          };
        }
      } else {
        return {};
      }
    });
  }

  yaxisAnnotationsFromGraphDataForSyncedPrograms(
    graphData: GraphDataModel,
    series: GraphSeriesModel
  ) {
    var annotationsForYAxis = graphData?.Annotations?.filter(
      (annotation) =>
        annotation.GraphAnnotationType === GraphAnnotationType.YAxis ||
        annotation.GraphAnnotationType === GraphAnnotationType.YAxisRange
    );

    // we need to filter the annotations to only include the ones for the target being graphed
    if (series.PatientTargetID) {
      annotationsForYAxis = annotationsForYAxis?.filter(
        (annotation) =>
          annotation.PatientTargetIDs?.includes(series.PatientTargetID!) ||
          (annotation.PatientProgramIDs?.length ?? 0) > 0
      );
    }

    return annotationsForYAxis?.map((annotation) => {
      if (annotation.VisibleForUser) {
        if (annotation.GraphAnnotationType === GraphAnnotationType.YAxis) {
          return {
            y: annotation.YValueStart,
            strokeDashArray: 5,
            borderColor: annotation.Color ?? '#008FFB',
            strokeOpacity: 0.3,
            borderWidth: 1,
            label: {
              borderColor: annotation.Color ?? '#008FFB',
              style: {
                color: annotation.Color ? `${annotation.Color}80` : '#008FFB80',
                fontSize: '14px',
                opacity: 0.2,
                background: 'rgba(255, 255, 255, 0.05)',
              },
              text: annotation.Label + ' - ' + annotation.YValueStart,
            },
          };
        } else {
          return {
            y: annotation.YValueStart,
            y2: annotation.YValueEnd,
            strokeDashArray: 5,
            borderColor: annotation.Color ?? '#008FFB',
            strokeOpacity: 0.3,
            borderWidth: 1,
            fillOpacity: 0.1,
            fillColor: annotation.Color ?? '#008FFB',
            label: {
              borderColor: annotation.Color ?? '#008FFB',
              style: {
                color: annotation.Color ? `${annotation.Color}80` : '#008FFB80',
                fontSize: '14px',
                opacity: 0.2,
                background: 'rgba(255, 255, 255, 0.05)',
              },
              text:
                annotation.Label +
                ' - ' +
                annotation.YValueStart +
                ' to ' +
                annotation.YValueEnd,
            },
          };
        }
      } else {
        return {};
      }
    });
  }

  pointsAnnotationsFromGraphDataForSyncedPrograms(
    graphData: GraphDataModel,
    series: GraphSeriesModel
  ) {
    return [];
  }

  textsAnnotationsFromGraphDataForSyncedPrograms(
    graphData: GraphDataModel,
    series: GraphSeriesModel
  ) {
    return [];
  }

  /**
   * Formats annotation label text to use multiple lines if it exceeds 30 characters
   */
  protected override formatAnnotationLabelText(
    text: string
  ): string | string[] {
    if (text.length <= 30) {
      return text;
    }

    // Split the text into words and filter out empty strings
    const words = text.split(' ').filter((word) => word.length > 0);
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      // If adding this word would exceed 30 characters, start a new line
      if (currentLine.length + word.length + 1 > 30 && currentLine.length > 0) {
        lines.push(currentLine);
        currentLine = word;
      } else {
        if (currentLine.length === 0) {
          currentLine = word;
        } else {
          currentLine += ' ' + word;
        }
      }
    }

    // Add the last line if it has content
    if (currentLine.length > 0) {
      lines.push(currentLine);
    }

    // Return array of strings for multiline support in ApexCharts
    // Pad each line to exactly 30 characters with whitespace
    if (lines.length > 1) {
      return lines.map((line) => line.padEnd(30, ' '));
    }

    return text;
  }

  /**
   * Handles print click from the graph toolbar
   */
  override handleDownloadPngClick(): void {
    this.setPngLoadingState(true);
    if (!this.graphData()) {
      this.setPngLoadingState(false);
      return;
    }

    this.waitForExportRender(this.isGrouped())
      .then(() => this.buildReportHeaderInfo())
      .then(async (header) => {
        const isGrouped = this.isGrouped();
        if (isGrouped) {
          if (!this.chart) return;
          await this.printService.downloadPngPage(this.chart as any, header);
        } else {
          const charts = this.chartList ? this.chartList.toArray() : [];
          if (charts.length === 0) return;
          await this.printService.downloadPngPageForCharts(charts as any, header);
        }
      })
      .finally(() => {
        this.setPngLoadingState(false);
      });
  }

  override handleDownloadPdfClick(): void {
    if (!this.graphData()) return;
    this.buildHeaderInfoAndDownloadPdf();
  }

  private async buildReportHeaderInfo(): Promise<PrintHeaderInfo> {
    let patientName = 'Unknown Patient';
    try {
      const patient = await this.staffApiService.GetPatientInfo({ id: Number(this.patientID()) }).pipe(take(1)).toPromise();
      if (patient) {
        patientName = `${patient.FirstName || ''} ${patient.LastName || ''}`.trim() || patient.PatientName || 'Unknown Patient';
      }
    } catch {}
    const startDate = this.startDate();
    const endDate = this.endDate();
    const timePeriod = `${this.datePipe.transform(startDate, 'MMM dd, yyyy')} - ${this.datePipe.transform(endDate, 'MMM dd, yyyy')}`;
    const graphType = this.getProgramGraphTypeDisplayName();
    const subtitleItems: string[] = [];
    if (this.selectedProgram()) subtitleItems.push(`${this.selectedProgram()!.ProgramName || 'Unnamed Program'}`);
    if (this.selectedTargets && this.selectedTargets.length > 0) subtitleItems.push(...this.selectedTargets.map(t => `${t.TargetName || 'Unnamed Target'} (${Utils.patientTargetTypeLabel(t.TargetType!)})`));
    const selectedTargets = this.selectedTargets.map(t => t.TargetName || 'Unknown Target');
    const visibleAnnotations = this.annotations()
      .filter(annotation => {
        if (!annotation.VisibleForUser) return false;
        if (annotation.StartDate) {
          const annotationDate = new Date(annotation.StartDate);
          return annotationDate >= startDate && annotationDate <= endDate;
        }
        return true;
      })
      .map(annotation => (this.annotationLabel(annotation)));
    const headerInfo: PrintHeaderInfo = {
      graphTitle: this.graphData()?.Title || 'Program Graph',
      graphType,
      componentType: 'Program Graph',
      patientName,
      timePeriod,
      groupingLabel: this.groupingOptionLabel(),
      displayOptions: this.getDisplayOptions(),
      subtitleItems,
      selectedTargets,
      selectedProgram: this.selectedProgram()?.ProgramName || undefined,
      annotations: visibleAnnotations,
      staffMember: this.getStaffMember() ?? undefined
    };
    return headerInfo;
  }

  private groupingOptionLabel(): string {
    const groupingMap: { [key in GraphGrouping]: string } = {
      [GraphGrouping.BySession]: 'By Session',
      [GraphGrouping.Daily]: 'Daily',
      [GraphGrouping.Weekly]: 'Weekly',
      [GraphGrouping.Monthly]: 'Monthly',
      [GraphGrouping.Yearly]: 'Yearly',
      [GraphGrouping.ByStaffMember]: 'By Staff Member',
      [GraphGrouping.ByTrial]: 'By Trial',
    };
    return groupingMap[this.groupingOption];
  }

  private getDisplayOptions(): string[] {
    const options: string[] = [];
    if (this.shouldDisplayToggleGrouped()) {
      if (this.isGrouped()) options.push('Grouped'); else options.push('Synced');
    }
    return options;
  }

  private getStaffMember(): string | undefined {
    return this.selectedStaffMember?.FullName ?? undefined;
  }

  private async buildHeaderInfoAndDownloadPdf(): Promise<void> {
    this.setPdfLoadingState(true);
    try {
      await this.waitForExportRender(this.isGrouped());
      const headerInfo: PrintHeaderInfo = await this.buildReportHeaderInfo();
      const isGrouped = this.isGrouped();
      if (isGrouped) {
        if (!this.chart) return;
        await this.printService.downloadPdf(this.chart as any, headerInfo);
      } else {
        const charts = this.chartList ? this.chartList.toArray() : [];
        if (charts.length === 0) return;
        await this.printService.downloadPdfForCharts(charts as any, headerInfo);
      }
    } finally {
      this.setPdfLoadingState(false);
    }
  }

  private setPngLoadingState(isLoading: boolean): void {
    this.ngZone.run(() => {
      this.isPrintingPng = isLoading;
      this.updateGraph();
    });
  }

  private setPdfLoadingState(isLoading: boolean): void {
    this.ngZone.run(() => {
      this.isPrintingPdf = isLoading;
      this.updateGraph();
    });
  }

  private waitForExportRender(isGrouped: boolean): Promise<void> {
    const delayMs = isGrouped
      ? this.groupedExportPreparationDelayMs
      : this.syncedExportPreparationDelayMs;

    return new Promise((resolve) => {
      setTimeout(() => resolve(), delayMs);
    });
  }

  /**
   * Gets the display name for the current program graph type
   */
  private getProgramGraphTypeDisplayName(): string {
    if (this.selectedProgramGraphType === null || this.selectedProgramGraphType === undefined) {
      return 'Unknown Graph Type';
    }

    const graphTypeMap: { [key in ProgramGraphType]: string } = {
      [ProgramGraphType.AllTargets]: 'All Targets',
      [ProgramGraphType.FilteredByTargetType]: 'Filtered by Target Type',
      [ProgramGraphType.FilteredByTargetStatus]: 'Filtered by Target Status',
      [ProgramGraphType.SelectedTargets]: 'Selected Targets',
      [ProgramGraphType.TargetsMasteredCumulative]: 'Targets Mastered Cumulative'
    };

    return graphTypeMap[this.selectedProgramGraphType] || 'Unknown Graph Type';
  }

}
